/**
 * @unscoped — TaskPage 의 i18n — Cycle 58.
 *
 * cycle 52 의 local LOCAL_MESSAGES (TaskPage.tsx 4 strings) 를 lib 모듈로 graduation +
 * 잔여 5 components (KanbanBoard / ProjectSidebar / TaskCard / TaskListView / TaskCreateModal)
 * 통합. cycle 51 calendar-i18n / cycle 54 mail-i18n / cycle 55 onboarding-i18n /
 * cycle 57 board-i18n 패턴 일관.
 *
 * graduation 기준: local dict 가 4+ string 또는 cross-component 키 공유 필요 시점 →
 * lib/<domain>-i18n.ts 도입. cycle 52 의 "4 strings 라 local 유지" 의 사이클 후속 정착.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== TaskPage 메인 (cycle 52 LOCAL_MESSAGES 마이그레이션) =====
  'task.err_fetch_projects': {
    ko: '프로젝트 조회 실패',
    en: 'Failed to load projects',
    ja: 'プロジェクト取得失敗',
  },
  'task.err_fetch_tasks': {
    ko: '태스크 조회 실패',
    en: 'Failed to load tasks',
    ja: 'タスク取得失敗',
  },
  'task.empty_projects_hint': {
    ko: '프로젝트가 없습니다. 좌측에서 새 프로젝트를 생성하세요.',
    en: 'No projects yet. Create one from the left sidebar.',
    ja: 'プロジェクトがありません。左サイドバーから新規作成してください。',
  },
  'task.retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },

  // ----- TaskPage UI -----
  'task.progress.aria': { ko: '진행률 {percent}%', en: 'Progress {percent}%', ja: '進捗 {percent}%' },
  'task.btn.new': { ko: '새 태스크', en: 'New task', ja: '新規タスク' },
  'task.err.status_change': {
    ko: '상태 변경 실패: {msg}',
    en: 'Status change failed: {msg}',
    ja: '状態変更失敗: {msg}',
  },
  // 판 7 (2026-10-13) — 완료 승인 게이트(PATCH 403 TASK_APPROVAL_REQUIRED)에 막혔을 때.
  'task.err.approval_required': {
    ko: '승인을 받아야 완료되는 프로젝트입니다. 「내 할일」에서 [완료 요청]을 눌러 주세요.',
    en: 'This project requires approval to finish a task. Use [Request approval] in My tasks.',
    ja: 'このプロジェクトは承認を受けて完了になります。「マイタスク」で[完了申請]を押してください。',
  },

  // ===== 공통 버튼 =====
  'task.btn.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'task.btn.save': { ko: '저장', en: 'Save', ja: '保存' },
  'task.btn.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'task.btn.saving_short': { ko: '저장…', en: 'Saving…', ja: '保存...' },
  'task.btn.close': { ko: '닫기', en: 'Close', ja: '閉じる' },

  // ===== ProjectSidebar =====
  'task.sidebar.heading': { ko: '프로젝트', en: 'Projects', ja: 'プロジェクト' },
  'task.sidebar.new_aria': { ko: '새 프로젝트', en: 'New project', ja: '新規プロジェクト' },
  'task.sidebar.placeholder_name': { ko: '프로젝트 이름', en: 'Project name', ja: 'プロジェクト名' },
  'task.sidebar.empty': { ko: '프로젝트가 없습니다', en: 'No projects', ja: 'プロジェクトがありません' },
  'task.err.create_fallback': { ko: '생성 실패', en: 'Create failed', ja: '作成失敗' },

  // ===== KanbanBoard =====
  'task.kanban.add_in_column_aria': {
    ko: '{column} 컬럼에 새 태스크',
    en: 'New task in {column}',
    ja: '{column} カラムに新規タスク',
  },
  'task.kanban.empty_column': { ko: '태스크 없음', en: 'No tasks', ja: 'タスクなし' },

  // ===== TaskCard =====
  'task.card.priority_tooltip': {
    ko: '우선순위 {label}',
    en: 'Priority: {label}',
    ja: '優先度 {label}',
  },
  'task.card.assignee_count_tooltip': {
    ko: '담당자 {n}명',
    en: 'Assignees: {n}',
    ja: '担当者 {n}名',
  },
  'task.card.assignee_count_short': { ko: '{n}명', en: '{n} people', ja: '{n}名' },
  'task.card.due_tooltip': { ko: '마감 {date}', en: 'Due {date}', ja: '期限 {date}' },

  // ===== TaskListView =====
  'task.list.col.status': { ko: '상태', en: 'Status', ja: '状態' },
  'task.list.col.title': { ko: '제목', en: 'Title', ja: 'タイトル' },
  'task.list.col.priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'task.list.col.assignee': { ko: '담당자', en: 'Assignee', ja: '担当者' },
  'task.list.col.due': { ko: '마감', en: 'Due', ja: '期限' },
  'task.list.col.tags': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'task.list.empty': { ko: '태스크가 없습니다', en: 'No tasks', ja: 'タスクがありません' },
  'task.list.status_change_aria': {
    ko: '{title} 상태 변경',
    en: 'Change status of {title}',
    ja: '{title} の状態を変更',
  },

  // ===== TaskCreateModal =====
  'task.create.aria': { ko: '새 태스크', en: 'New task', ja: '新規タスク' },
  'task.create.heading': { ko: '새 태스크', en: 'New task', ja: '新規タスク' },
  'task.create.label_title': { ko: '제목', en: 'Title', ja: 'タイトル' },
  'task.create.label_status': { ko: '상태', en: 'Status', ja: '状態' },
  'task.create.label_priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'task.create.label_due': { ko: '마감', en: 'Due', ja: '期限' },
  'task.create.label_tags': { ko: '태그 (쉼표로 구분)', en: 'Tags (comma-separated)', ja: 'タグ (カンマ区切り)' },
  'task.create.placeholder_tags': { ko: '예: urgent, frontend', en: 'e.g. urgent, frontend', ja: '例: urgent, frontend' },
  'task.create.label_description': { ko: '설명', en: 'Description', ja: '説明' },
  'task.create.err_title': { ko: '제목을 입력하세요.', en: 'Please enter a title.', ja: 'タイトルを入力してください。' },
  'task.create.err_save_fallback': { ko: '저장 실패', en: 'Save failed', ja: '保存失敗' },
  // v2.6.1 (2026-06-11) — 새 할일 모달 상세화: 섹션 그룹 + 카운터 + 빠른 마감일 + 태그 미리보기
  'task.create.section_basic': { ko: '기본 정보', en: 'Basic info', ja: '基本情報' },
  'task.create.section_detail': { ko: '상세 설정', en: 'Details', ja: '詳細設定' },
  'task.create.title_placeholder': { ko: '예: 로그인 버그 수정', en: 'e.g. Fix login bug', ja: '例: ログインバグ修正' },
  'task.create.desc_placeholder': { ko: '작업 내용이나 완료 조건 (선택)', en: 'What to do or definition of done (optional)', ja: '作業内容や完了条件（任意）' },
  'task.create.due_quick_1w': { ko: '+1주', en: '+1 wk', ja: '+1週' },
  'task.create.due_quick_2w': { ko: '+2주', en: '+2 wks', ja: '+2週' },
  'task.create.due_quick_1m': { ko: '+1개월', en: '+1 mo', ja: '+1ヶ月' },
  'task.create.due_clear': { ko: '지우기', en: 'Clear', ja: 'クリア' },
  'task.create.due_quick_aria': { ko: '마감일 빠른 선택', en: 'Quick due date', ja: '期限のクイック選択' },
  'task.create.btn_create': { ko: '할일 만들기', en: 'Create task', ja: 'タスク作成' },
  'task.create.title_counter_aria': { ko: '제목 글자수', en: 'Title length', ja: 'タイトルの文字数' },
  'task.create.tags_preview_aria': { ko: '입력된 태그 미리보기', en: 'Parsed tags preview', ja: '入力タグのプレビュー' },
  // v2.7 (2026-06-11, 시안 ⑤+C 확정) — 상단 프로젝트 카드 바 (사이드바 대체)
  'task.topbar.aria': { ko: '프로젝트 선택', en: 'Select project', ja: 'プロジェクト選択' },
  'task.topbar.new': { ko: '새 프로젝트', en: 'New project', ja: '新規プロジェクト' },
  'task.topbar.personal': { ko: '개인', en: 'Personal', ja: '個人' },
  'task.topbar.card_aria': { ko: "'{name}' 프로젝트 선택", en: "Select project '{name}'", ja: "プロジェクト'{name}'を選択" },
  'task.topbar.more_members': { ko: '+{n}', en: '+{n}', ja: '+{n}' },
  'task.topbar.delete_aria': { ko: "'{name}' 프로젝트 삭제", en: "Delete project '{name}'", ja: "プロジェクト'{name}'を削除" },
  // 2026-07-24 — ⑵차 좌측 '내 프로젝트' 패널 (레이아웃 SSOT all-frame.html 3d 반영, 상단 바 대체)
  'task.side.heading': { ko: '내 프로젝트', en: 'My projects', ja: 'マイプロジェクト' },
  'task.side.back_home': { ko: '프로젝트 홈', en: 'Project home', ja: 'プロジェクトホーム' },
  'task.side.aria': { ko: '내 프로젝트 목록', en: 'My project list', ja: 'マイプロジェクト一覧' },
  'task.side.group_active': { ko: '진행 중', en: 'In progress', ja: '進行中' },
  'task.side.group_done': { ko: '완료', en: 'Completed', ja: '完了' },
  'task.side.badge_active': { ko: '진행', en: 'Active', ja: '進行' },
  'task.side.badge_done': { ko: '완료', en: 'Done', ja: '完了' },
  'task.side.collapse': { ko: '프로젝트 패널 접기', en: 'Collapse project panel', ja: 'プロジェクトパネルを折りたたむ' },
  'task.side.expand': { ko: '프로젝트 패널 펼치기', en: 'Expand project panel', ja: 'プロジェクトパネルを展開' },
  'task.side.due': { ko: '~{date}', en: 'due {date}', ja: '~{date}' },
  'task.side.dday': { ko: 'D-{n}', en: 'D-{n}', ja: 'D-{n}' },
  // ⛔ 2026-10-02 — 값이 '기한 초과' 였다. /task 좌측 패널만 그렇게 말하고 프로젝트 메뉴의
  //   다섯 자리는 'D+{n}' 이었다(사용자 결정으로 D+{n} 통일).
  //   ⚠ 아래 task.toolbar.f_due_overdue 는 ★ 바꾸지 않았다 — 그것은 거르개의 «범주 이름»이고
  //     형제가 '전체 · 오늘 · 이번 주' 라 평말이어야 한다. 그리고 ⛔ 거기엔 넘길 n 이 없다.
  'task.side.dday_over': { ko: 'D+{n}', en: 'D+{n}', ja: 'D+{n}' },
  'task.side.dday_today': { ko: 'D-DAY', en: 'D-DAY', ja: 'D-DAY' },
  'task.side.progress_aria': { ko: "'{name}' 진행률 {n}%", en: "'{name}' progress {n}%", ja: "'{name}' 進捗 {n}%" },
  // 2026-07-24 — 팀 스코프 (design-20260724-project-team-scope D-1)
  'task.scope.label': { ko: '팀', en: 'Team', ja: 'チーム' },
  'task.scope.all': { ko: '내 팀 전체', en: 'All my teams', ja: 'マイチーム全体' },
  'task.scope.personal': { ko: '개인', en: 'Personal', ja: '個人' },
  'task.scope.aria': { ko: '팀 스코프 선택', en: 'Select team scope', ja: 'チームスコープ選択' },
  'task.scope.new_team': { ko: '새 팀 만들기', en: 'New team', ja: '新規チーム作成' },
  'task.scope.empty': { ko: '이 팀에 참여 중인 프로젝트가 없습니다', en: 'No projects you joined in this team', ja: 'このチームで参加中のプロジェクトがありません' },
  // v2.7.1 — 헤더 내비게이션 (대시보드/팀 이동)
  'task.nav.dashboard': { ko: '프로젝트 대시보드', en: 'Project dashboard', ja: 'プロジェクトダッシュボード' },
  'task.nav.teams': { ko: '내 팀', en: 'My teams', ja: 'マイチーム' },
  'task.title.link_aria': { ko: "'{name}' 링크 새 창에서 열기", en: "Open '{name}' link in new tab", ja: "'{name}'のリンクを新しいタブで開く" },
  // v2.7.2 (2026-06-11) — 칸반 카드 클릭 → 태스크 상세 레이어팝업
  'task.detail.heading': { ko: '태스크 상세', en: 'Task detail', ja: 'タスク詳細' },
  'task.detail.tags_label': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'task.detail.tags_readonly_hint': { ko: '태그는 생성 시 지정됩니다.', en: 'Tags are set at creation.', ja: 'タグは作成時に指定します。' },
  'task.detail.readonly_hint': { ko: '작성자만 수정할 수 있습니다 — 코멘트는 작성할 수 있어요.', en: 'Only the author can edit — you can still comment.', ja: '編集は作成者のみ — コメントは可能です。' },
  'task.detail.assignee_edit_hint': { ko: '담당자는 상태·완료일자만 변경할 수 있습니다.', en: 'As an assignee, you can change status and due date.', ja: '担当者はステータス・期日のみ変更できます。' },
  'task.detail.assignee_label': { ko: '담당자', en: 'Assignees', ja: '担当者' },
  'task.detail.assignee_no_team': { ko: '무소속(개인) 프로젝트는 담당자를 지정할 수 없어요 — 팀 프로젝트에서만 가능합니다.', en: 'Assignees are available only in team projects.', ja: '担当者はチームプロジェクトのみ指定可能です。' },
  'task.detail.assignee_empty': { ko: '선택 가능한 팀원이 없습니다.', en: 'No team members available.', ja: '選択可能なメンバーがいません。' },
  'task.detail.assignee_gone_tooltip': { ko: '팀에서 제외된 사용자 — 클릭 시 담당 해제', en: 'No longer a team member — click to unassign', ja: 'チーム外ユーザー — クリックで解除' },
  'task.detail.created_at': { ko: '생성일 {date}', en: 'Created {date}', ja: '作成日 {date}' },
  'task.detail.assignees': { ko: '담당 {n}명', en: '{n} assignee(s)', ja: '担当{n}名' },
  'task.detail.btn_delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'task.detail.delete_confirm': { ko: "'{title}' 태스크를 삭제할까요?", en: "Delete task '{title}'?", ja: "タスク'{title}'を削除しますか？" },
  'task.detail.err_delete': { ko: '태스크 삭제 실패', en: 'Failed to delete task', ja: 'タスク削除失敗' },
  'task.card.open_aria': { ko: "'{title}' 상세 열기", en: "Open detail of '{title}'", ja: "'{title}'の詳細を開く" },
  // v2.8 (2026-06-11) — 파일 첨부(생성 모달) + 코멘트(상세 모달)
  'task.attach.label': { ko: '파일 첨부 (선택)', en: 'Attachments (optional)', ja: 'ファイル添付（任意）' },
  'task.attach.add': { ko: '파일 선택', en: 'Choose files', ja: 'ファイル選択' },
  'task.attach.hint': { ko: '최대 20개 — 선택 즉시 업로드되고 태스크 생성 시 첨부됩니다.', en: 'Up to 20 — uploaded on select, attached on create.', ja: '最大20件 — 選択時にアップロードされ、作成時に添付。' },
  'task.attach.uploading': { ko: '업로드 중…', en: 'Uploading…', ja: 'アップロード中…' },
  'task.attach.remove': { ko: '제거', en: 'Remove', ja: '削除' },
  'task.attach.err': { ko: "'{name}' 업로드 실패", en: "Failed to upload '{name}'", ja: "'{name}'のアップロード失敗" },
  'task.detail.attachments': { ko: '첨부 파일', en: 'Attachments', ja: '添付ファイル' },
  'task.detail.attach_add': { ko: '파일 추가', en: 'Add file', ja: 'ファイル追加' },
  'task.detail.download': { ko: '다운로드', en: 'Download', ja: 'ダウンロード' },
  'task.detail.attach_delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'task.detail.attach_empty': { ko: '첨부 파일이 없습니다.', en: 'No attachments.', ja: '添付はありません。' },
  'task.detail.comments': { ko: '코멘트', en: 'Comments', ja: 'コメント' },
  'task.detail.comment_placeholder': { ko: '코멘트를 입력하세요', en: 'Write a comment', ja: 'コメントを入力' },
  'task.detail.comment_send': { ko: '등록', en: 'Post', ja: '登録' },
  'task.detail.comment_delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'task.detail.comment_delete_confirm': { ko: '이 코멘트를 삭제할까요?', en: 'Delete this comment?', ja: 'このコメントを削除しますか？' },
  'task.detail.comment_empty': { ko: '아직 코멘트가 없습니다. 첫 코멘트를 남겨보세요.', en: 'No comments yet. Be the first.', ja: 'まだコメントがありません。' },
  'task.detail.comment_err': { ko: '코멘트 처리 실패', en: 'Comment action failed', ja: 'コメント処理失敗' },
  'task.detail.attach_err': { ko: '첨부 처리 실패', en: 'Attachment action failed', ja: '添付処理失敗' },
  // ===== 2026-08-18 시안 — 헤더 팀 배지 · 보드 툴바 · 하단 진행현황 도크 =====
  'task.teamdrop.summary': { ko: '내 팀 · {n}명', en: 'My team · {n}', ja: 'マイチーム · {n}名' },
  'task.teamdrop.open_aria': { ko: '팀원 목록 열기', en: 'Open member list', ja: 'メンバー一覧を開く' },
  'task.teamdrop.invite': { ko: '팀원 초대', en: 'Invite member', ja: 'メンバー招待' },
  'task.teamdrop.me': { ko: '나', en: 'me', ja: '自分' },
  'task.teamdrop.empty': { ko: '표시할 팀원이 없습니다.', en: 'No members to show.', ja: '表示するメンバーがいません。' },
  'task.toolbar.view_aria': { ko: '보기 방식', en: 'View mode', ja: '表示方式' },
  'task.toolbar.view_list': { ko: '목록보기', en: 'List', ja: 'リスト表示' },
  'task.toolbar.view_kanban': { ko: '칸반보기', en: 'Kanban', ja: 'かんばん表示' },
  'task.toolbar.filter': { ko: '필터', en: 'Filter', ja: 'フィルター' },
  'task.toolbar.filter_aria': { ko: '업무 필터 열기', en: 'Open task filter', ja: 'タスクフィルターを開く' },
  'task.toolbar.search_placeholder': { ko: '업무 검색', en: 'Search tasks', ja: 'タスク検索' },
  'task.toolbar.search_aria': { ko: '업무 검색', en: 'Search tasks', ja: 'タスク検索' },
  'task.toolbar.f_scope': { ko: '담당', en: 'Assignee', ja: '担当' },
  'task.toolbar.f_scope_all': { ko: '전체', en: 'All', ja: '全体' },
  'task.toolbar.f_scope_mine': { ko: '내 것만', en: 'Mine', ja: '自分のみ' },
  'task.toolbar.f_priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'task.toolbar.f_all': { ko: '전체', en: 'All', ja: '全体' },
  'task.toolbar.f_due': { ko: '마감', en: 'Due', ja: '期限' },
  'task.toolbar.f_due_overdue': { ko: '기한 초과', en: 'Overdue', ja: '期限超過' },
  'task.toolbar.f_due_today': { ko: '오늘', en: 'Today', ja: '今日' },
  'task.toolbar.f_due_week': { ko: '이번 주', en: 'This week', ja: '今週' },
  'task.toolbar.reset': { ko: '필터 초기화', en: 'Reset filters', ja: 'フィルター初期化' },
  'task.toolbar.result': { ko: '{n}건 표시', en: '{n} shown', ja: '{n}件表示' },
  'task.dock.summary': { ko: '팀 진행 현황 · 팀원 {m}명 · 진행중 {n}건', en: 'Team progress · {m} members · {n} in progress', ja: 'チーム進捗 · メンバー{m}名 · 進行中{n}件' },
  'task.dock.expand': { ko: '펼치기', en: 'Expand', ja: '展開' },
  'task.dock.collapse': { ko: '접기', en: 'Collapse', ja: '折りたたむ' },

  // ===== 사용자 정의 칸 · 칸 팝오버 (2차 4-1 · 2026-10-08) =====
  //   ⭐ 정본(feature/dev-syk)의 선언을 ★ «괄호 맞춰» 그대로 옮겼다 — ⛔ 손으로 적지 않았다.
  //   ⚠ 이 열쇠는 ★ 깃발 PROJECT_V2 가 «켜질 때»만 쓰인다 ⇒ 꺼진 동안 ⭐ 보이는 글자 0.
  //   ⚠ 통이 ★ task-i18n 이다(⛔ project-i18n 이 아니다) — ⭐ 그 셋이 `tTask` 를 쓴다.
  'task.cell.add_start': { ko: '＋ 시작일 추가', en: '+ Add start date', ja: '＋ 開始日を追加' },
  'task.cell.assignee_aria': { ko: '담당자 지정', en: 'Assign', ja: '担当者を指定' },
  'task.cell.assignee_search_ph': { ko: '이름 또는 메일 주소로 검색', en: 'Search by name or email', ja: '名前・メールで検索' },
  'task.cell.clear_all': { ko: '모두 지우기', en: 'Clear all', ja: 'すべて消去' },
  'task.cell.clear_one': { ko: '선택 해제', en: 'Clear', ja: '選択を解除' },
  'task.cell.due_aria': { ko: '기한 지정', en: 'Set due date', ja: '期限を指定' },
  'task.cell.member_aria': { ko: "'{field}' 담당 지정", en: "Set '{field}'", ja: "'{field}' を指定" },
  'task.cell.next_month': { ko: '다음 달', en: 'Next month', ja: '次の月' },
  'task.cell.no_member': { ko: '표시할 팀원이 없습니다.', en: 'No members.', ja: 'メンバーがいません。' },
  'task.cell.picking_start': { ko: '시작일 고르는 중 — 날짜를 누르세요', en: 'Picking start date — choose a day', ja: '開始日を選択中 — 日付を押してください' },
  'task.cell.prev_month': { ko: '이전 달', en: 'Previous month', ja: '前の月' },
  'task.cell.today': { ko: '오늘', en: 'Today', ja: '今日' },
  'task.cell.year_month': { ko: '{y}년 {m}월', en: '{y}/{m}', ja: '{y}年{m}月' },
  /*
   * ⭐⭐ 2026-10-09 (2차 4-5) — ★ 칸 «종류 이름» ⭐ 아홉. ⇒ 정본 선언을 «글자 그대로» 옮겼다.
   *
   * ## ⛔⛔ 이 아홉이 ★ 없어서 ⇒ ⭐ 메뉴에 «열쇠 글자»가 그려지고 있었다 (인사 세션이 찾았다)
   * ```
   * pages/ProjectV2/custom-field-ui.tsx:26
   *   FIELD_TYPE_LABEL 의 값이 ★ 'task.field.type_single' … 아홉인데 ⇒ ⛔ 그 열쇠가 ★ 0건이었다
   * 쓰는 곳 :204 · :212 · :228  ⇒  tTask(FIELD_TYPE_LABEL[t] ★ as never)
   * ⇒ ⇒ ★ `tTask` 는 없는 열쇠에 ⭐ «열쇠 자신»을 돌려준다(:275 `if (!m) return key`)
   *   ⇒ ⇒ ⇒ ★ 그래서 ⭐ 「task.field.type_single」이 ★ 메뉴에 그대로 그려졌다
   * ```
   * ⇒ ⭐ 고침은 ★ «둘»이다 — ⇒ ⛔ 하나만 하면 ★ 다시 꺼진다:
   *   ① ★ 이 아홉을 ⭐ 더한다 (⇒ 여기)
   *   ② ⭐ `FIELD_TYPE_LABEL` 의 값 타입을 ★ `TaskI18nKey` 로 좁히고 ⇒ `as never` 를 ★ 뗀다
   * ⇒ ⚠⚠ 그런데 ★ ② 가 ⭐ «아직 일하지 않는다» — ⇒ 2026-10-09 에 ★ 돌려서 쟀다:
   * ```
   * 이 파일 :23   const MESSAGES: Record<string, Record<Lang, string>> = { … }
   * 이 파일 :266  export type TaskI18nKey = keyof typeof MESSAGES;
   * ⇒ ⇒ ⛔ `Record<string, …>` 의 `keyof` 는 ★ `string` 이다 ⇒ ⇒ ⭐ TaskI18nKey ＝ string
   *   ⇒ ⇒ ★ 사보타주: 아래 한 줄을 지우고 ⇒ `tsc --noEmit -p tsconfig.app.json` ⇒ ⇒ ⛔ exit=0 (안 울린다)
   * ```
   *   ⇒ ⭐ 그래서 ★ 이 아홉의 잠금은 ⇒ ⭐ «시험»이 쥔다(⇒ `pages/ProjectV2/task-board.guard.test.ts`),
   *     ⇒ ⇒ ★ `tsc` 가 ⛔ 아니다. ⇒ ⇒ ⭐ 타입으로 잠그는 판은 ★ «별 판»으로 올린다(관제 승인).
   *   ⇒ ⚠ 그 별 판이 할 일 ⇒ 이 파일 :23 을 `as const satisfies …` 로 바꾸기.
   *
   * ## ⭐⭐ 2026-10-10 — ★ 그 별 판이 ⇒ ⭐ «섰다» (project-i18n 에 대해) ⇒ PR #2400
   *
   * ⇒ ✅ `project-i18n.ts` 는 ★ 2026-10-10 에 ⭐ 바뀌었다 — ⇒ ⛔ 더 이상 ★ 「같은 꼴」이 아니다:
   * ```
   * project-i18n.ts:85    const MESSAGES = {
   * project-i18n.ts:1167  } satisfies Record<string, Record<Lang, string>>;
   * ```
   * ⇒ ⭐ 그 판에서 ★ 쟀다(⇒ 이 글의 ② 와 ⭐ 같은 사보타주를 ★ 세 판에 나란히):
   * ```
   *                     ① 성한 소스  ② 없는 열쇠를 부름  ③ ★ 언어 한 칸이 빠진 선언
   *   Ⓐ 떼기 전          0 ✅        ⛔ ⭐ 0건           ✅ TS2741 @선언 자리
   *   Ⓑ 주석만 뗌        0 ✅        ✅ TS2345           ⚠ TS7053×2 ＋ TS7006×2 @조회 자리
   *   Ⓒ satisfies        0 ✅        ✅ TS2345           ✅ TS2741 @선언 자리
   * ```
   * ⇒ ⇒ ⭐⭐ ★ Ⓑ③ 이 ⇒ ⭐ 이 글이 예고하지 못한 것이다 — ★ 그 주석은 ⇒ ⭐ 「ja 가 빠진 선언」을
   *   ★ 막고 있었고, ⇒ ⛔ «그냥 떼면» 그 오류가 ★ 선언 자리를 떠나 ⇒ ⭐ 조회 자리의 «암묵적 any»
   *   가 된다 ⇒ ⇒ ⚠ 「무엇이 빠졌다」고 ★ 말해 주지 않는다. ⇒ ✅ 그래서 ★ `satisfies` 다.
   * ⇒ ⚠ 그리고 ★ 잠긴 것은 ⭐ «전부»가 아니다 ⇒ ⇒ ★ 752 / 765 였다 —
   *   ⇒ ⭐ 남은 12 자리는 ★ 호출자가 ⭐ «제 손으로 붙인» `as never`·`as ProjectI18nKey` 라
   *     ⇒ ⇒ ⛔ 사전을 고쳐도 ★ 안 닫힌다(⇒ 그 12 의 파일:줄은 `project-i18n.ts` 머리말에 있다).
   *     ⇒ ⇒ ⚠ 이 파일의 `FIELD_TYPE_LABEL[t] as never` 도 ★ **그 열두 중 하나와 같은 꼴**이다
   *       ⇒ ⇒ ⭐ 즉 위 ② 를 해도 ★ 그 자리는 ⇒ ⛔ 안 잠긴다. ⇒ ★ `as never` 를 ⭐ 떼야 한다.
   *
   * ## ⛔⛔ 2026-10-10 — ★ 이 글이 ⭐ «예고한 그대로» 일어났다 (⇒ 가장 값진 줄)
   *
   * ⇒ ★ 이 글은 ⭐ 「이 아홉의 잠금은 ⇒ ⭐ «시험»이 쥔다 · `tsc` 가 ⛔ 아니다」라고 적었다.
   *   ⇒ ⇒ ⭐⭐ 그것이 ★ 참이었다 — ⇒ ★ 같은 꼴의 결함이 ⭐ **또** 났고, ⇒ 이번엔 ★ 시험이 없었다:
   * ```
   * ⛔ project.team.aw.add_aria  ⇒ 선언 0 인데 ⇒ 세 자리에서 불렸다
   *      pages/ProjectPage/TeamInvitePickerModal.tsx:290 · 360 · 429  의 aria-label
   *   ⇒ tProject 도 모르는 열쇠에 «열쇠 자신»을 돌려준다 (project-i18n.ts 의 tProject)
   *   ⇒ ⇒ ⛔ 그래서 ★ 화면 낭독기가 ⇒ ⭐ 그 열쇠 이름을 ★ 소리내어 읽었다
   * ⇒ 내가 쟀다 ⇒ origin/main 에서 ★ 선언 0 · 호출 3 · 명시 타입을 떼면 ⭐ tsc 3건
   * ⇒ 운영 반영 ⇒ ★ 42차 (⇒ 관제 확인 — ⛔ 내가 운영을 직접 재지는 않았다)
   * ```
   * ⇒ ⭐ 메운 것은 ★ 2026-10-10 정본 이관(PR #2396) ⇒ ⇒ ★ «눈»을 세운 것은 ⭐ PR #2400.
   * ⇒ ⚠ 그 아홉(`task.field.type_*`)에는 ★ 시험이 ⭐ 있었고(`task-board.guard.test.ts`),
   *   ⇒ `project.team.aw.*` 에는 ⭐ **없었다** ⇒ ⇒ ★ 그 차이가 ⭐ 이 글의 주장을 ★ 증명했다.
   *
   * ## ⚠ 2026-10-10 — ★ 이 글의 ③ 을 ⭐ 바로잡는다: 「나머지 셋」이 ⇒ ⛔ 셋이 아니다
   *
   * ⇒ ★ 쟀다(`git grep -ln <그 선언> -- apps/frontend/src/lib` · ⇒ 레포 뿌리에서):
   * ```
   * src/lib/*-i18n.ts        28통
   *   ⛔ 아직 «옛 꼴»          21   ← ⇒ 이 글이 적은 「셋」이 아니라 ⭐ 스물하나다
   *   ✅ satisfies            2   ⇒ project-i18n(#2400 · 2026-10-10) · reservation-i18n(2026-09-08)
   *   ⇒ 그 밖 꼴              5   ⇒ admin-i18n:147 `as const` 만(꼴 검사 ⛔ 없음)
   *                               · app-download-i18n:82 `as const satisfies …`
   *                               · employment-type-i18n:17 열쇠가 union(⇒ 이미 눈이 있다)
   *                               · screen-lock-i18n:13 `DICT: Record<Lang, …>`(언어가 앞)
   *                               · base-i18n(사전 없음)
   * ⇒ 합 21 ＋ 2 ＋ 5 = 28 ✅ · lib «밖»의 옛 꼴 = 1 (⇒ task-board.guard.test.ts 안의 «인용»)
   * ```
   * ⇒ ⚠ 그러니 ★ 「셋만 고치면 끝」이 ⭐ 아니다. ⇒ ⇒ ⛔ 그리고 ★ 한 통에 ⭐ 3×3 한 벌이 필요하다
   *   (⇒ 통마다 ★ 「그 주석이 ⭐ 무엇을 사고 있었나」가 ★ 다를 수 있다) ⇒ ⇒ ★ 작은 일이 ⭐ 아니다.
   *   ⇒ ⭐ 관제 결정(2026-10-10) ⇒ ★ 이관이 끝난 뒤 ⇒ ⭐ **한 통에 한 PR**.
   *
   * ## ⭐ 그래서 ★ 이 파일 :23 을 할 사람이 ⇒ ⭐ «먼저 정할» 것
   *
   * ⇒ ⚠ 이 레포에 ★ 꼴이 ⭐ **둘** 있다:
   * ```
   * reservation-i18n.ts:223   } as const satisfies Record<string, Record<Lang, string>>;   ← 2026-09-08
   * project-i18n.ts:1167      } satisfies Record<string, Record<Lang, string>>;            ← 2026-10-10 (#2400)
   * ```
   * ⇒ ★ 이 글의 ④ 는 ⭐ `as const satisfies` 를 적었는데 ⇒ ⇒ #2400 은 ★ `as const` 를 ⭐ 안 붙였다.
   *   ⇒ ⭐ `keyof` 가 리터럴 유니온이 되는 것은 ★ `satisfies` ⭐ 하나로 된다(#2400 에서 실측).
   *   ⇒ ⚠ `as const` 를 더하면 ★ 값이 ⭐ `readonly` 가 된다 ⇒ ⇒ ★ 그러면 ⭐ 내보낸 사전을 받는
   *     쪽에 닿는다(`PROJECT_I18N_MESSAGES` ⇒ 받는 곳 3 — ★ 모두 ⭐ 시험이다).
   *     ⇒ ⇒ ⭐ 그 영향을 ★ 2026-10-10 에 ⇒ ⭐ 쟀다 — ⇒ 아래 ⓙ. ⇒ ⇒ ⚠ 한 줄로 ⭐ 미리:
   *       ★ 값을 치르는 통은 ⇒ ⭐ `project-i18n` **하나**이고 ⇒ ⇒ ★ 이 파일은 ⭐ 사전을 «안» 내보내
   *       ⇒ ⇒ ✅ :23 에 `as const` 를 붙여도 ★ 닿을 곳이 ⭐ 0 이다.
   *
   * ### ⭐ 관제 기울기 (2026-10-10) ⇒ ★ `satisfies` «하나»로 — ⛔ `as const` 는 ★ 안 붙인다
   *
   *   까닭 ① ★ 목적(`keyof` 가 ⭐ 리터럴 유니온)은 ⇒ ★ `satisfies` 하나로 ⭐ 선다 (#2400 실측)
   *        ② ⚠ `as const` 는 ★ 값을 ⭐ `readonly` 로 만들어 ⇒ ⇒ ★ 「타입에 눈을 준다」가
   *           ⇒ ⭐ «남의 코드를 바꾸는 일»로 ★ 번진다
   *        ③ ⭐ 작은 쪽이 ★ 되돌리기 쉽다
   *   ⇒ ⚠ 그래도 ★ 그것은 ⭐ «기울기»다 — ⇒ ⭐ 그 판에서 ★ «재서» 정한다. ⇒ ⇒ 그때 할 일 ⭐ 셋:
   *
   *     ⓘ ★ 꼴을 ⭐ «먼저» 정하고 ★ 적은 뒤에 ⇒ ⭐ 21 을 손댄다 ⇒ ⇒ ⛔ 섞어 가지 말 것
   *        (⇒ ★ 바로 위 절이 ⭐ 그 「먼저 정할 것」이다)
   *
   *     ⓙ ⭐ `as const` 를 고르면 ⇒ ★ `readonly` 가 ⭐ «닿는 곳»을 ★ 수로 먼저.
   *        ⇒ 2026-10-10 에 ★ 쟀다 (⇒ ⚠ 주석을 ⭐ 길이 보존으로 덮고 셌다 — ★ 이 머리말이
   *          ⇒ ⇒ 그 이름을 ⭐ 인용하고 있어 ★ 날것으로 세면 ⇒ ⛔ 한 건이 부풀었다):
   *        ```
   *        i18n 통 28 중 ★ 사전을 «내보내는» 것 ⇒ ⭐ 3
   *          PROJECT_I18N_MESSAGES      ⇒ 받는 파일 ⭐ 2 (★ 둘 다 시험) · 쓰는 자리 3
   *                                       pages/ProjectV2/task-detail.guard.test.ts
   *                                       pages/ProjectV2/task-widgets.guard.test.ts
   *          NOTIFICATION_I18N_MESSAGES ⇒ 받는 곳 ⭐ 0 (⇒ 레포 «전체»에서 제 파일뿐)
   *          RESERVATION_I18N_MESSAGES  ⇒ 받는 곳 ⭐ 0 (⇒ 같음)
   *        ⇒ ⭐⭐ 그리고 ★ 이 파일은 ⇒ ⭐ 사전을 «안» 내보낸다
   *          (내보내는 것 ⇒ TaskI18nKey · tTask · getTaskLang · setTaskLang · subscribeTaskLang)
   *          ⇒ ⇒ ✅ 즉 ★ :23 에 `as const` 를 붙여도 ⇒ ⭐ 닿을 곳이 ★ 0 이다
   *        ```
   *        ⇒ ⚠ 그러니 ★ 비용은 ⭐ «통마다 다르다» — ⇒ 값을 치르는 것은 ★ `project` ⭐ 하나뿐이고,
   *          ⇒ ⇒ 그 둘은 ★ 시험이라 ⇒ ⭐ 우리 관문(`tsconfig.app.json`)이 ⛔ 보지 않는다
   *          ⇒ ⇒ ⇒ ⚠ 즉 ★ 깨져도 ⭐ 관문이 ⛔ 조용하다 ⇒ ⇒ ★ 시험까지 넣는 판으로 ⭐ 한 번 재라.
   *
   *     ⓚ ⭐ `satisfies` 하나로 고르면 ⇒ ★ `reservation-i18n.ts:223` 의 `as const` 도 ⭐ 함께 떼서
   *        ⇒ ⇒ ★ 꼴을 ⭐ «하나»로 만든다. ⇒ ⚠ 선례는 ★ 그쪽이다 — ⭐ 2026-09-08(`f85734436`)부터다.
   *        ⇒ ⇒ ⛔ 두 꼴을 ★ 남기면 ⇒ ⭐ 다음 사람이 ★ 또 캔다 — ⇒ ⚠ 이 절이 ⭐ 그 캐기의 기록이다.
   *
   * ⏳ ★ 만료 조건 ⇒ ⭐ **「이 파일 :23 이 ★ satisfies 가 되는 날 ⇒ ⭐ 이 글의 ② 와 ④ 를 지운다」**
   *   ⇒ ⛔ 종전 「그 셋 중 하나라도」로 두면 ⇒ ⭐ 2026-10-10 에 ★ 이미 참이 되어 ⇒ ⇒ 이 글이
   *     ★ 사라진다. ⇒ ⇒ ⭐ 그런데 ★ 위 ⭐ 「예고한 그대로 일어났다」 절은 ⇒ ★ 남아야 한다.
   *   ⇒ ⭐ 그때도 ★ 지우지 «말» 것 ⇒ ⇒ ⭐ ①(실제 사고) · ③(통 수) · 위 두 절은 ★ 기록이다.
   */
  'task.field.type_single': { ko: '단일 선택', en: 'Single select', ja: '単一選択' },
  'task.field.type_multi': { ko: '다중 선택', en: 'Multi select', ja: '複数選択' },
  'task.field.type_date': { ko: '날짜', en: 'Date', ja: '日付' },
  'task.field.type_status': { ko: '상태', en: 'Status', ja: '状態' },
  'task.field.type_text': { ko: '텍스트', en: 'Text', ja: 'テキスト' },
  'task.field.type_number': { ko: '숫자', en: 'Number', ja: '数値' },
  'task.field.type_url': { ko: 'URL', en: 'URL', ja: 'URL' },
  'task.field.type_checkbox': { ko: '체크박스', en: 'Checkbox', ja: 'チェックボックス' },
  'task.field.type_member': { ko: '멤버', en: 'Member', ja: 'メンバー' },
  'task.field.add_aria': { ko: '커스텀 필드 추가', en: 'Add custom field', ja: 'カスタムフィールド追加' },
  'task.field.add_heading': { ko: '커스텀 필드 추가', en: 'Add custom field', ja: 'カスタムフィールド追加' },
  'task.field.create': { ko: '필드 만들기', en: 'Create field', ja: 'フィールド作成' },
  'task.field.delete_aria': { ko: "'{name}' 필드 삭제", en: "Delete field '{name}'", ja: "'{name}'フィールド削除" },
  'task.field.manage': { ko: '커스텀 필드 설정', en: 'Custom field settings', ja: 'カスタムフィールド設定' },
  'task.field.manage_empty': { ko: '아직 만든 필드가 없습니다.', en: 'No custom fields yet.', ja: 'まだフィールドがありません。' },
  'task.field.name_label': { ko: '필드 이름', en: 'Field name', ja: 'フィールド名' },
  'task.field.options_label': { ko: '선택지 (쉼표로 구분)', en: 'Options (comma separated)', ja: '選択肢（カンマ区切り）' },
  'task.field.options_ph': { ko: '예: 대기, 진행, 완료', en: 'e.g. Todo, Doing, Done', ja: '例: 待機, 進行, 完了' },
  'task.field.rename_aria': { ko: "'{name}' 필드 이름 바꾸기", en: "Rename field '{name}'", ja: "'{name}'フィールド名を変更" },
  'task.field.status_defaults': { ko: '대기, 진행, 완료', en: 'Pending, In progress, Done', ja: '待機, 進行, 完了' },
  'task.field.unit_custom': { ko: '직접 입력', en: 'Custom…', ja: '直接入力' },
  'task.field.unit_label': { ko: '단위', en: 'Unit', ja: '単位' },
  'task.field.unit_none': { ko: '없음', en: 'None', ja: 'なし' },
  'task.field.unit_ph': { ko: '예: 만원', en: 'e.g. USD', ja: '例: 万円' },
  'task.field.unit_presets': {
    ko: '원, %, 개, 건, 시간, 일, 점, kg',
    en: '$, %, ea, items, hrs, days, pts, kg',
    ja: '円, %, 個, 件, 時間, 日, 点, kg',
  },

  // ===== 정본 이관 판 0 — 보드(/task) 공통 열쇠 (2026-10-13) =====
  //   ⭐ 정본(feature/dev-syk dd05a6fcd)의 선언을 ★ «괄호 맞춰» 그대로 옮겼다 — 손으로 적지 않았다.
  //   ⚠ 쓰는 화면(TaskTableView · task-viz · 칸반 카드 삭제 · 빈 스페이스 안내 · 상세 «추가 정보»)은
  //     서브 메뉴 판에서 들어온다 ⇒ 이 판만으로는 ★ 보이는 글자 0.
  'task.empty_scope_title': {
    ko: '표시할 프로젝트가 없습니다',
    en: 'No projects to show',
    ja: '表示するプロジェクトがありません',
  },
  'task.empty_scope_hint': {
    ko: '이 스페이스의 프로젝트가 모두 지워졌거나 아직 없습니다. 새 프로젝트를 만들거나 프로젝트 홈으로 돌아가세요.',
    en: 'This space has no projects yet, or they were all deleted. Create one or go back to the project home.',
    ja: 'このスペースのプロジェクトはまだないか、すべて削除されました。新規作成するかプロジェクトホームに戻ってください。',
  },
  'task.empty_scope_new': { ko: '새 프로젝트 만들기', en: 'New project', ja: '新規プロジェクト' },
  'task.kanban.meter_aria': {
    ko: '{label} {n}건, 그중 지연 {over}건',
    en: '{label}: {n} tasks, {over} overdue',
    ja: '{label} {n}件、うち遅延 {over}件',
  },
  'task.kanban.column_overdue': { ko: '지연 {n}', en: '{n} overdue', ja: '遅延 {n}' },
  'task.card.delete': { ko: '업무 삭제', en: 'Delete task', ja: '業務を削除' },
  'task.card.delete_aria': { ko: "'{title}' 업무 삭제", en: "Delete task '{title}'", ja: "'{title}' を削除" },
  'task.card.delete_irreversible': { ko: '되돌릴 수 없습니다.', en: 'This cannot be undone.', ja: '元に戻せません。' },
  'task.card.delete_err_forbidden': {
    ko: '태스크 삭제 권한이 없습니다 (작성자 또는 관리자만 가능).',
    en: 'No permission to delete this task (creator or manager only).',
    ja: 'タスクの削除権限がありません（作成者または管理者のみ）。',
  },
  'task.dock.done_pct': { ko: '완료 {n}%', en: '{n}% done', ja: '完了 {n}%' },
  'task.table.col_task': { ko: '업무', en: 'Task', ja: 'タスク' },
  'task.table.col_assignee': { ko: '담당자', en: 'Assignee', ja: '担当者' },
  'task.table.col_priority': { ko: '우선순위', en: 'Priority', ja: '優先度' },
  'task.table.col_start': { ko: '시작일', en: 'Start', ja: '開始日' },
  'task.table.col_progress': { ko: '진행률', en: 'Progress', ja: '進捗' },
  'task.table.col_tags': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'task.table.col_due': { ko: '마감', en: 'Due', ja: '期限' },
  'task.table.more': { ko: '+ {n}건 더 보기', en: '+ {n} more', ja: '+ {n}件もっと見る' },
  'task.table.overdue': { ko: '{n}일 지연', en: '{n} days overdue', ja: '{n}日遅延' },
  'task.detail.extra': { ko: '추가 정보', en: 'Additional info', ja: '追加情報' },
  'task.detail.extra_hint': {
    ko: '목록보기 표의 ＋ 로 만든 항목입니다. 여기서 고치면 표에도 바로 반영됩니다.',
    en: 'Columns added with ＋ in the list view. Changes here show up in the table right away.',
    ja: 'リスト表示の ＋ で追加した項目です。ここで直すと表にもすぐ反映されます。',
  },
  'task.detail.extra_err': { ko: '추가 정보를 저장하지 못했습니다.', en: 'Could not save the field.', ja: '追加情報を保存できませんでした。' },
};

export type TaskI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 58 — TaskPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) — calendar/workspace/mail/onboarding/board 와 일관.
 */
export function tTask(key: TaskI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getTaskLang(): Lang { return getSharedLang(); }
export function setTaskLang(value: Lang): void { setSharedLang(value); }
export function subscribeTaskLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
